import { observer } from "mobx-react-lite";
import { ChevronDown } from "lucide-react";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import OrgBreadcrumb, {
  useOrgPathWithStatus,
} from "@/components/common/OrgBreadcrumb";
import { useDashboardFiltersStore } from "../hooks/useDashboardStore";

const OverviewOrgRow = observer(({ row, isExpanded, onToggleDetail }) => {
  const { t } = useTran();
  const { available } = useDashboardFiltersStore();
  const breadcrumb = (row.path || []).join(" > ");
  const meta = getUnitTypeMeta(row.unitLevel);
  const Icon = meta.icon;
  const metricCount =
    row.metricsWithValue ?? row.metricsWithData ?? row.metricsBoth ?? 0;

  const { pathWithStatus, orgStatus, orgInactiveDate } = useOrgPathWithStatus(
    row.orgId,
    available.orgTree,
  );

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-card/95 p-3">
      <div className="flex min-w-0 items-center gap-3">
        <span
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border/80 bg-muted/40",
            meta.color,
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-1.5">
            <p className="truncate text-sm font-semibold text-foreground">
              {row.orgName}
            </p>
            <OrgStatusTag
              status={orgStatus}
              inactiveDate={orgInactiveDate}
              className="text-[10px] shrink-0"
            />
          </div>
          <OrgBreadcrumb
            pathWithStatus={pathWithStatus}
            fallbackLabel={breadcrumb}
          />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className="whitespace-nowrap rounded-full border border-border/70 bg-muted/40 px-2.5 py-1 text-xs text-foreground">
          {t("Metrics")}: {metricCount}
        </span>
        {onToggleDetail && (
          <button
            type="button"
            onClick={onToggleDetail}
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
          >
            {t("View Detail")}
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          </button>
        )}
      </div>
    </div>
  );
});

export default OverviewOrgRow;
